import { useEffect } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { ContentLayout } from '../Layouts/ContentLayout/ContentLayout';
import { NoResults } from '@/components/NoResults/NoResults';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import NoTransactionsIcon from '../../images/NoTransactionsIcon.svg?react';
import { Paginator } from '@/components/Paginator/Paginator';
import { useInterval } from '@/hooks/useInterval';
import { PageFooter } from '@/components/Page/PageFooter/PageFooter';
import { RecipientWiresList } from './ui/RecipientWiresList';
import { isPendingWireStatus } from '@/helpers/wireHelpers';
import { AdBanner } from '@/bundle/shared/components/banner/AdBanner/AdBanner';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { useHasAnyOrganizationAdminRole } from '@/bundle/shared/hooks/useHasAnyOrganizationAdminRole';
import { Box } from '@/components/Box/Box';
import { SearchInput } from '@/bundle/shared/components/SearchInput/SearchInput';
import { NoSearchResults } from '../shared/components/NoSearchResults';
import { useSearch } from '../shared/hooks/useSearch';
import { loadRecipientWires } from '@/bundle/_Recipient/api/api';
import { getSearchListData } from '@/helpers/helpers';

const WIRES_POLLING_INTERVAL = 30000; // 30 seconds;

export const RecipientWiresPage = () => {
  const { search, setSearch, clearSearch, submitSearch, searchParams } = useSearch<{ name: string }>('name');
  const queryClient = useQueryClient();
  const { hasAnyOrganizationAdminRole } = useHasAnyOrganizationAdminRole();

  const { data, isPending } = useQuery({
    queryKey: ['load_recipient_wires', searchParams],
    queryFn: () => loadRecipientWires(searchParams),
    placeholderData: keepPreviousData,
  });

  const handleSubmitSearch = () => {
    if (!!search.length && searchParams.name !== search) {
      amplitudeService.logEvent(AMPLITUDE_EVENTS.RecipientSearchSubmit);
    }

    submitSearch();
  };

  const { list, hasData, hasPagination, totalCount, noResults, noSearchResults } = getSearchListData(
    data?.body,
    searchParams,
    isPending
  );
  const hasWireInPendingStatus = list?.some(({ status }) => isPendingWireStatus(status));

  // Polling wires list logic
  const { cancelInterval, isIntervalRunning } = useInterval(() => {
    if (hasData && hasWireInPendingStatus) {
      queryClient.invalidateQueries({ queryKey: ['load_recipient_wires'] });
    }
  }, WIRES_POLLING_INTERVAL);

  // Cancel polling logic
  useEffect(() => {
    const shouldCancelInterval = hasData && !hasWireInPendingStatus && isIntervalRunning;

    if (shouldCancelInterval) {
      cancelInterval();
    }
  }, [hasData, hasWireInPendingStatus, cancelInterval, isIntervalRunning]);

  return (
    <ContentLayout
      title='Wires'
      breadcrumbs='Wires'
      subtitle='View the wires assigned to you. Click on the wire card to provide wire info for a particular wire.'
      banner={hasAnyOrganizationAdminRole ? null : <AdBanner />}
      offsetSize='small'
    >
      <Box display='flex' alignItems='flex-start' columnGap='20px' flexWrap='wrap'>
        <Box width='360px' mobile={{ width: '100%' }}>
          <SearchInput
            placeholder='Search by wire name'
            value={search}
            onChange={(value) => setSearch(value)}
            onClear={clearSearch}
            onSubmit={handleSubmitSearch}
          />
        </Box>
      </Box>
      <PageBody>
        {hasData && <RecipientWiresList list={list} />}
        {noResults && <NoResults icon={<NoTransactionsIcon />} message='There is no wire yet.' />}
        {noSearchResults && <NoSearchResults />}
      </PageBody>
      {hasPagination && (
        <PageFooter>
          <Paginator page={searchParams.page} limit={searchParams.limit} total={totalCount} />
        </PageFooter>
      )}
    </ContentLayout>
  );
};
